import React, { useState, useEffect } from "react";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import HCMore from "highcharts/highcharts-more";
import boxplotData from "./boxplot.json";

if (typeof Highcharts === "object") {
  HCMore(Highcharts);
}

const BoxPlotMedicare = () => {
  const [rawData, setRawData] = useState({});

  useEffect(() => {
    const transformedData = {};
    Object.entries(boxplotData).forEach(([code, entries]) => {
      const rates = entries
        .map((item) => parseFloat(item.medicare_percentage))
        .filter((v) => !isNaN(v) && v >= 0);
      if (rates.length > 0) {
        transformedData[code] = { data: rates };
      }
    });
    setRawData(transformedData);
  }, []);

  const getBoxStats = (arr) => {
    const sorted = arr
      .filter((v) => !isNaN(v) && isFinite(v))
      .sort((a, b) => a - b);
    if (sorted.length === 0) return [0, 0, 0, 0, 0];
    const q1 = sorted[Math.floor((sorted.length - 1) * 0.25)];
    const median = sorted[Math.floor((sorted.length - 1) * 0.5)];
    const q3 = sorted[Math.floor((sorted.length - 1) * 0.75)];
    return [sorted[0], q1, median, q3, sorted[sorted.length - 1]];
  };

  const categories = Object.keys(rawData);
  if (categories.length === 0)
    return <div className="p-6 text-yellow-600">No data available.</div>;

  const boxData = categories.map((code) => getBoxStats(rawData[code].data));

  const outliers = categories.flatMap((code, i) => {
    const [min, q1, median, q3, max] = boxData[i];
    const data = rawData[code].data;
    const iqr = q3 - q1;
    const lowerBound = q1 - 1.5 * iqr;
    const upperBound = q3 + 1.5 * iqr;
    return data
      .filter((v) => v < lowerBound || v > upperBound)
      .map((v) => [i, v]);
  });

  const boxColors = [
    "#A8D8EA",
    "#98D8AA",
    "#86C7CC",
    "#B5EAD7",
    "#9ACEEB",
    "#C7E9C0",
    "#FFDAC1",
    "#E2F0CB",
  ];

  // Dark grey color palette
  const darkGrey = "#555555";
  const mediumGrey = "#777777";
  const lightGrey = "#999999";

  const options = {
    chart: {
      type: "boxplot",
      backgroundColor: "#FFFFFF",
      height: 600,
      spacing: [10, 10, 15, 10],
    },
    title: {
      text: "Medicare Percentage Distribution by Billing Code",
      style: {
        fontSize: "20px",
        fontWeight: "bold",
        fontFamily: "Arial, sans-serif",
        color: "#333333",
      },
    },
    credits: {
      enabled: false,
    },
    exporting: {
      enabled: false,
    },
    legend: {
      enabled: false,
    },
    xAxis: {
      categories,
      labels: {
        rotation: 0,
        style: {
          fontSize: "11px",
          fontWeight: "normal",
          color: darkGrey,
        },
        step: Math.max(1, Math.floor(categories.length / 20)),
        y: 20,
      },
      title: {
        text: "Billing Codes",
        style: {
          fontSize: "14px",
          fontWeight: "bold",
          color: darkGrey,
        },
      },
      lineWidth: 1,
      lineColor: lightGrey,
      tickLength: 0,
    },
    yAxis: {
      title: {
        text: "Medicare Percentage (%)",
        style: {
          fontSize: "14px",
          fontWeight: "bold",
          color: darkGrey,
        },
      },
      labels: {
        format: "{value}%",
        style: {
          color: darkGrey,
        },
      },
      min: 0,
      gridLineWidth: 1,
      gridLineColor: "#f0f0f0",
      lineWidth: 1,
      lineColor: lightGrey,
    },
    plotOptions: {
      boxplot: {
        fillColor: "#FFFFFF",
        lineWidth: 2,
        medianColor: darkGrey,
        medianWidth: 3,
        stemColor: mediumGrey,
        stemDashStyle: "Solid",
        stemWidth: 1.5,
        whiskerColor: mediumGrey,
        whiskerLength: "70%",
        whiskerWidth: 1.5,
        pointWidth: 40,
        colorByPoint: true,
        states: {
          hover: {
            lineWidth: 2,
            brightness: 0.05,
          },
        },
      },
      scatter: {
        marker: {
          radius: 4,
          symbol: "circle",
          lineColor: "#FD444d",
          lineWidth: 1,
          fillColor: "#FDA4AF",
        },
      },
    },
    series: [
      {
        name: "Medicare %",
        type: "boxplot",
        data: categories.map((code, index) => {
          const stats = getBoxStats(rawData[code].data);
          return {
            x: index,
            low: stats[0],
            q1: stats[1],
            median: stats[2],
            q3: stats[3],
            high: stats[4],
            color: boxColors[index % boxColors.length],
            fillColor: boxColors[index % boxColors.length] + "80",
            borderColor: boxColors[index % boxColors.length],
            borderWidth: 3,
          };
        }),
        tooltip: {
          headerFormat: '<span style="font-size: 14px; color: #E76B2A; font-weight: 600; margin-bottom: 5px">{point.key}</span><br/>',
          pointFormat: 'Min: <b>{point.low}%</b><br/>' +
            'Q1: <b>{point.q1}%</b><br/>' +
            'Median: <b>{point.median}%</b><br/>' +
            'Q3: <b>{point.q3}%</b><br/>' +
            'Max: <b>{point.high}%</b>'
        },
      },
      {
        name: "Outliers",
        type: "scatter",
        data: outliers,
        marker: {
          radius: 4,
          symbol: "circle",
          lineColor: "#FD444d",
          lineWidth: 1,
          fillColor: "#FDA4AF",
        },
        // FIX: Add empty headerFormat to remove the blue circle
        tooltip: {
          headerFormat: '', // This removes the colored circle
          pointFormat: 'Outlier: <b>{point.y}%</b>'
        },
      },
    ],
    tooltip: {
      shared: true,
      useHTML: true,
      backgroundColor: "rgba(255, 255, 255, 0.98)",
      borderWidth: 1,
      borderColor: "#e0e0e0",
      borderRadius: 5,
      shadow: true,
      style: {
        color: "#555555",
      },
      // Alternative global fix: override the formatter completely
      formatter: function() {
        // For box plot series
        if (this.point.low !== undefined) {
          return `
            <div style="font-weight: 600; color: #E76B2A; margin-bottom: 5px; font-size: 14px;">
              ${categories[this.point.x]}
            </div>
            <div>
              Min: <b>${this.point.low}%</b><br/>
              Q1: <b>${this.point.q1}%</b><br/>
              Median: <b>${this.point.median}%</b><br/>
              Q3: <b>${this.point.q3}%</b><br/>
              Max: <b>${this.point.high}%</b>
            </div>
          `;
        }
        // For outliers
        if (this.point.y !== undefined) {
          return `Outlier: <b>${this.point.y}%</b>`;
        }
        return '';
      }
    },
  };

  return (
    <div
      className="p-6 border-1"
      style={{
        width: "97%",
        margin: "auto",
        borderRadius: "12px",
        backgroundColor: "#FFFFFF",
      }}
    >
      <div
        style={{
          padding: "20px",
          backgroundColor: "#FFFFFF",
          borderRadius: "8px",
          boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
        }}
      >
        <HighchartsReact highcharts={Highcharts} options={options} />
      </div>
    </div>
  );
};

export default BoxPlotMedicare;